import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import React from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import Header from './components/Header';
import Notification from './components/Notification';
import Sidebar from './components/Sidebar';
import AuthGuard from './components/AuthGuard';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import Categories from './pages/Categories';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductEdit from './pages/ProductEdit';
import CategoryEdit from './pages/CategoryEdit';
import SubcategoryEdit from './pages/SubcategoryEdit';
import Subcategories from './pages/Subcategories';
import Tvas from './pages/Tvas';
import Brands from './pages/Brands';
import BrandEdit from './pages/BrandEdit';
import Families from './pages/Families';
import FamilyEdit from './pages/FamilyEdit';
import Promotions from './pages/Promotions';
import PromotionEdit from './pages/PromotionEdit';
import PromotionStats from './pages/PromotionStats';
import Grades from './pages/Grades';
import GradeEdit from './pages/GradeEdit';
import Orders from './pages/Orders';
import Users from './pages/Users';
import News from './pages/News';
import NewsEdit from './pages/NewsEdit';
import Notifications from './pages/Notifications';
import LoyaltySettings from './pages/LoyaltySettings';
import PaymentMethods from './pages/PaymentMethods';
import AffiliateRequests from './pages/AffiliateRequests';
import Affiliates from './pages/Affiliates';
import AffiliateCampaigns from './pages/AffiliateCampaigns';
import AffiliateProductRequests from './pages/AffiliateProductRequests';
import AffiliateReviews from './pages/AffiliateReviews';
import Statistics from './pages/Statistics';
import PriceComparison from './pages/PriceComparison';
import ShippingMethods from './pages/ShippingMethods';
import ShippingRates from './pages/ShippingRates';
import KitsManagement from './pages/KitsManagement';
import XpConfigurations from './pages/XpConfigurations';
import XpConfigurationEdit from './pages/XpConfigurationEdit';
import Login from './pages/Login';
import Stock from './pages/Stock';
import Suppliers from './pages/Suppliers';
import SupplierEdit from './pages/SupplierEdit';
import SupplierOrders from './pages/SupplierOrders';
import SupplierOrderEdit from './pages/SupplierOrderEdit';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#333333',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
  },
});

function AppContent() {
  const { notification, hideNotification } = useNotification();

  return (
    <Router>
      <Routes>
        {/* Routes publiques */}
        <Route path="/login" element={<Login />} />

        {/* Routes protégées */}
        <Route path="/*" element={
          <AuthGuard>
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
              <Sidebar />
              <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Header />
                <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/statistics" element={<Statistics />} />
                    <Route path="/price-comparison" element={<PriceComparison />} />
                    <Route path="/shipping-methods" element={<ShippingMethods />} />
                    <Route path="/shipping-rates" element={<ShippingRates />} />
                    <Route path="/kits" element={<KitsManagement />} />
                    <Route path="/stock" element={<Stock />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/new" element={<ProductEdit />} />
                    <Route path="/products/edit/:id" element={<ProductEdit />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/categories/new" element={<CategoryEdit />} />
                    <Route path="/categories/edit/:id" element={<CategoryEdit />} />
                    <Route path="/subcategories" element={<Subcategories />} />
                    <Route path="/subcategories/new" element={<SubcategoryEdit />} />
                    <Route path="/subcategories/edit/:id" element={<SubcategoryEdit />} />
                    <Route path="/tvas" element={<Tvas />} />
                    <Route path="/brands" element={<Brands />} />
                    <Route path="/brands/new" element={<BrandEdit />} />
                    <Route path="/brands/edit/:id" element={<BrandEdit />} />
                    <Route path="/families" element={<Families />} />
                    <Route path="/families/new" element={<FamilyEdit />} />
                    <Route path="/families/edit/:id" element={<FamilyEdit />} />
                    <Route path="/promotions" element={<Promotions />} />
                    <Route path="/promotions/new" element={<PromotionEdit />} />
                    <Route path="/promotions/edit/:id" element={<PromotionEdit />} />
                    <Route path="/promotions/:id/stats" element={<PromotionStats />} />
                    <Route path="/grades" element={<Grades />} />
                    <Route path="/grades/new" element={<GradeEdit />} />
                    <Route path="/grades/edit/:id" element={<GradeEdit />} />
                    <Route path="/loyalty" element={<LoyaltySettings />} />
                    <Route path="/xp-configurations" element={<XpConfigurations />} />
                    <Route path="/xp-configurations/new" element={<XpConfigurationEdit />} />
                    <Route path="/xp-configurations/edit/:id" element={<XpConfigurationEdit />} />
                    <Route path="/payment-methods" element={<PaymentMethods />} />
                    <Route path="/affiliate-requests" element={<AffiliateRequests />} />
                    <Route path="/affiliates" element={<Affiliates />} />
                    <Route path="/affiliate-campaigns" element={<AffiliateCampaigns />} />
                    <Route path="/affiliate-product-requests" element={<AffiliateProductRequests />} />
                    <Route path="/affiliate-reviews" element={<AffiliateReviews />} />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/news" element={<News />} />
                    <Route path="/news/new" element={<NewsEdit />} />
                    <Route path="/news/edit/:id" element={<NewsEdit />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/stock" element={<Stock />} />
                    <Route path="/suppliers" element={<Suppliers />} />
                    <Route path="/suppliers/:id" element={<SupplierEdit />} />
                    <Route path="/supplier-orders" element={<SupplierOrders />} />
                    <Route path="/supplier-orders/:id" element={<SupplierOrderEdit />} />
                  </Routes>
                </Box>
              </Box>
            </Box>
          </AuthGuard>
        } />
      </Routes>
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;



